import { FC, useEffect, useMemo, useState } from "react";
import classes from './tutorPaymentDetails.module.scss'
import { ITutorPaymentDetail, TTutorPaymentType } from "../../../entities/tutor";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useGlobalLoadingActions } from "../../../entities/globalLoading";
import { useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyButton } from "../../../shared/ui/button";
import { MyInput } from "../../../shared/ui/input";
import { userService } from "../../../entities/user";
import { Modal } from "../../../shared/ui/modal";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { useAppSelector } from "../../../app/store/store";
import { FEATURES, hasFeature } from "../../../entities/my";

interface IProps {
    userId: number;
    canAccess?: boolean;
    canEdit?: boolean;
    initialPaymentDetails?: ITutorPaymentDetail[];
    savePaymentDetails?: (paymentDetails: ITutorPaymentDetail[]) => Promise<void>;
    onSaveSuccess?: () => Promise<void> | void;
    saveDeleteImmediately?: boolean;
}

const paymentTypeLabels: Record<TTutorPaymentType, string> = {
    card: 'Карта',
    sbp: 'СБП',
    bank_account: 'Реквизиты',
}

let tempPaymentDetailId = -1

const createDetail = (type: TTutorPaymentType): ITutorPaymentDetail => ({
    id: tempPaymentDetailId--,
    type,
    recipient_name: '',
    card_number: '',
    phone_number: '',
    bank_name: '',
    account_number: '',
    bik: '',
    inn: '',
    is_priority: false,
})

const getDigits = (value: string, maxLength?: number) => {
    const digits = value.replace(/\D/g, '')
    return typeof maxLength === 'number' ? digits.slice(0, maxLength) : digits
}

const formatCardNumber = (value: string) =>
    getDigits(value, 19)
        .replace(/(.{4})/g, '$1 ')
        .trim()

const sanitizePhone = (value: string) => getDigits(value, 11)
const sanitizeAccountNumber = (value: string) => getDigits(value, 20)
const sanitizeBik = (value: string) => getDigits(value, 9)
const sanitizeInn = (value: string) => getDigits(value, 12)

const normalizePaymentDetails = (items: ITutorPaymentDetail[]) => items.map(item => ({
    ...item,
    card_number: formatCardNumber(item.card_number),
    phone_number: sanitizePhone(item.phone_number),
    account_number: sanitizeAccountNumber(item.account_number),
    bik: sanitizeBik(item.bik),
    inn: sanitizeInn(item.inn),
}))

const preparePaymentDetailsForRequest = (items: ITutorPaymentDetail[]) => {
    const priorityTypes = new Set<TTutorPaymentType>()

    return items.map(item => {
        const isPriority = item.is_priority && !priorityTypes.has(item.type)
        if(isPriority){
            priorityTypes.add(item.type)
        }

        return {
            ...item,
            card_number: getDigits(item.card_number, 19),
            phone_number: sanitizePhone(item.phone_number),
            account_number: sanitizeAccountNumber(item.account_number),
            bik: sanitizeBik(item.bik),
            inn: sanitizeInn(item.inn),
            is_priority: isPriority,
        }
    })
}

export const TutorPaymentDetails: FC<IProps> = ({
    userId,
    canAccess,
    canEdit,
    initialPaymentDetails,
    savePaymentDetails,
    onSaveSuccess,
    saveDeleteImmediately = true,
}) => {

    const [paymentDetails, setPaymentDetails] = useState<ITutorPaymentDetail[]>([])
    const [pendingDeleteDetail, setPendingDeleteDetail] = useState<ITutorPaymentDetail | null>(null)
    const [formError, setFormError] = useState('')
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsLoading} = useGlobalLoadingActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const isAccessAllowed = canAccess ?? hasFeature(my, FEATURES.PROFILE.PAYMENT_DETAILS_READ, userId)
    const isEditAllowed = canEdit ?? hasFeature(my, FEATURES.PROFILE.PAYMENT_DETAILS_UPDATE, userId)

    const setApiError = (e: unknown, defaultMessage: string) => {
        console.log(e)
        if(e instanceof AuthError){
            if(e.status === 401){
                setIsAuth(false)
            }
            setFormError(e.message)
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }
        if(e instanceof MyError){
            setFormError(e.message)
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }
        setFormError(defaultMessage)
        setGlobalMessage({message: defaultMessage, type: 'error'})
    }

    const getData = async () => {
        try{
            setIsLoading(true)
            setFormError('')
            const paymentDetailsRes = await userService.getPaymentDetails(userId)
            setPaymentDetails(normalizePaymentDetails(paymentDetailsRes))
        }
        catch(e){
            setApiError(e, 'Ошибка при получении реквизитов пользователя')
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if(initialPaymentDetails){
            setPaymentDetails(normalizePaymentDetails(initialPaymentDetails))
            setFormError('')
            return
        }
        if(isAccessAllowed && userId > 0){
            getData()
        }
    }, [initialPaymentDetails, isAccessAllowed, userId])

    const grouped = useMemo(() => {
        return {
            card: paymentDetails.filter(item => item.type === 'card'),
            sbp: paymentDetails.filter(item => item.type === 'sbp'),
            bank_account: paymentDetails.filter(item => item.type === 'bank_account'),
        }
    }, [paymentDetails])

    const updateDetail = (currentId: number, key: keyof ITutorPaymentDetail, value: string | boolean) => {
        setPaymentDetails(state => state.map(item =>
            item.id === currentId
                ? {...item, [key]: value}
                : item
        ))
    }

    const setPriority = (type: TTutorPaymentType, currentId: number) => {
        setPaymentDetails(state => state.map(item => {
            if(item.type !== type){
                return item
            }

            return {
                ...item,
                is_priority: item.id === currentId,
            }
        }))
    }

    const addDetail = (type: TTutorPaymentType) => {
        setPaymentDetails(state => [...state, createDetail(type)])
    }

    const onSave = async () => {
        const invalidCard = paymentDetails.find(item => item.type === 'card' && item.card_number && getDigits(item.card_number).length < 16)
        if(invalidCard){
            setGlobalMessage({message: 'Номер карты должен содержать от 16 до 19 цифр', type: 'error'})
            return
        }

        const invalidAccount = paymentDetails.find(item => item.type === 'bank_account' && item.account_number && item.account_number.length !== 20)
        if(invalidAccount){
            setGlobalMessage({message: 'Номер счета должен содержать 20 цифр', type: 'error'})
            return
        }

        try{
            setIsLoading(true)
            setFormError('')
            const nextPaymentDetails = preparePaymentDetailsForRequest(paymentDetails)
            if(savePaymentDetails){
                await savePaymentDetails(nextPaymentDetails)
            }
            else{
                await userService.savePaymentDetails(userId, nextPaymentDetails)
            }
            setGlobalMessage({message: 'Реквизиты сохранены', type: 'ok'})
            if(onSaveSuccess){
                await onSaveSuccess()
            }
            else{
                await getData()
            }
        }
        catch(e){
            setApiError(e, 'Ошибка при сохранении реквизитов')
        }
        finally{
            setIsLoading(false)
        }
    }

    const onDeleteDetail = async () => {
        if(!pendingDeleteDetail){
            return
        }

        const nextPaymentDetails = paymentDetails.filter(item => item.id !== pendingDeleteDetail.id)

        if(!saveDeleteImmediately){
            setPaymentDetails(nextPaymentDetails)
            setPendingDeleteDetail(null)
            setFormError('')
            return
        }

        if(pendingDeleteDetail.id < 0){
            setPaymentDetails(nextPaymentDetails)
            setPendingDeleteDetail(null)
            return
        }

        const invalidCard = nextPaymentDetails.find(item => item.type === 'card' && item.card_number && getDigits(item.card_number).length < 16)
        if(invalidCard){
            setGlobalMessage({message: 'Номер карты должен содержать от 16 до 19 цифр', type: 'error'})
            setPendingDeleteDetail(null)
            return
        }

        const invalidAccount = nextPaymentDetails.find(item => item.type === 'bank_account' && item.account_number && item.account_number.length !== 20)
        if(invalidAccount){
            setGlobalMessage({message: 'Номер счета должен содержать 20 цифр', type: 'error'})
            setPendingDeleteDetail(null)
            return
        }

        try{
            setIsLoading(true)
            setFormError('')
            const nextPaymentDetailsRequest = preparePaymentDetailsForRequest(nextPaymentDetails)
            if(savePaymentDetails){
                await savePaymentDetails(nextPaymentDetailsRequest)
            }
            else{
                await userService.savePaymentDetails(userId, nextPaymentDetailsRequest)
            }
            setPaymentDetails(nextPaymentDetails)
            setPendingDeleteDetail(null)
            setGlobalMessage({message: 'Реквизит удален', type: 'ok'})
            if(onSaveSuccess){
                await onSaveSuccess()
            }
            else{
                await getData()
            }
        }
        catch(e){
            setApiError(e, 'Ошибка при удалении реквизита')
        }
        finally{
            setIsLoading(false)
        }
    }

    const renderFields = (item: ITutorPaymentDetail) => {
        switch(item.type){
            case 'sbp':
                return (
                    <>
                        <MyInput
                            disabled={!isEditAllowed}
                            value={item.phone_number}
                            setValue={value => updateDetail(item.id, 'phone_number', sanitizePhone(value))}
                            title="Номер телефона"
                            inputMode="numeric"
                            placeholder="79991234567"
                        />
                        <MyInput disabled={!isEditAllowed} value={item.bank_name} setValue={value => updateDetail(item.id, 'bank_name', value)} title="Банк" />
                    </>
                )
            case 'bank_account':
                return (
                    <>
                        <MyInput
                            disabled={!isEditAllowed}
                            value={item.account_number}
                            setValue={value => updateDetail(item.id, 'account_number', sanitizeAccountNumber(value))}
                            title="Номер счета"
                            inputMode="numeric"
                            placeholder="20 цифр"
                        />
                        <MyInput
                            disabled={!isEditAllowed}
                            value={item.bik}
                            setValue={value => updateDetail(item.id, 'bik', sanitizeBik(value))}
                            title="БИК"
                            inputMode="numeric"
                            placeholder="9 цифр"
                        />
                        <MyInput
                            disabled={!isEditAllowed}
                            value={item.inn}
                            setValue={value => updateDetail(item.id, 'inn', sanitizeInn(value))}
                            title="ИНН"
                            inputMode="numeric"
                            placeholder="10 или 12 цифр"
                        />
                    </>
                )
            default:
                return (
                    <MyInput
                        disabled={!isEditAllowed}
                        value={item.card_number}
                        setValue={value => updateDetail(item.id, 'card_number', formatCardNumber(value))}
                        title="Номер карты"
                        inputMode="numeric"
                        placeholder="0000 0000 0000 0000"
                    />
                )
        }
    }

    const renderSection = (type: TTutorPaymentType) => (
        <section className={classes.section}>
            <section className={classes.sectionHeader}>
                <section className={classes.subtitle}>{paymentTypeLabels[type]}</section>
                {isEditAllowed && <MyButton onClick={() => addDetail(type)}>
                    Добавить
                </MyButton>}
            </section>
            {
                grouped[type].length
                    ?
                grouped[type].map(item =>
                    <section key={item.id} className={classes.card}>
                        <section className={classes.cardHeader}>
                            <label className={classes.priority}>
                                <input
                                    disabled={!isEditAllowed}
                                    checked={item.is_priority}
                                    onChange={() => setPriority(type, item.id)}
                                    type="radio"
                                    name={`priority_${type}`}
                                />
                                Приоритетный
                            </label>
                            {isEditAllowed && <button className={classes.remove} onClick={() => setPendingDeleteDetail(item)}>
                                Удалить
                            </button>}
                        </section>
                        <MyInput disabled={!isEditAllowed} value={item.recipient_name} setValue={value => updateDetail(item.id, 'recipient_name', value)} title="ФИО получателя" />
                        {renderFields(item)}
                    </section>
                )
                    :
                <section className={classes.empty}>
                    Пока нет реквизитов этого типа
                </section>
            }
        </section>
    )

    if(!isAccessAllowed){
        return null
    }

    return (
        <section className={classes.container}>
            {renderSection('card')}
            {renderSection('sbp')}
            {renderSection('bank_account')}
            {formError && <section className={classes.error}>*{formError}</section>}
            {isEditAllowed && <section className={classes.save}>
                <MyButton onClick={onSave}>
                    Сохранить реквизиты
                </MyButton>
            </section>}
            {isEditAllowed && <Modal open={Boolean(pendingDeleteDetail)} setOpen={(open) => !open && setPendingDeleteDetail(null)}>
                <ConfirmationAction
                    title={`Удалить реквизит "${paymentTypeLabels[pendingDeleteDetail?.type ?? 'card']}"?`}
                    setOpen={(open) => !open && setPendingDeleteDetail(null)}
                    onClick={onDeleteDetail}
                    type="delete"
                />
            </Modal>}
        </section>
    )
}
